/**
 * Платежи по СБП, которые ещё не стали заказом, — над активными заявками.
 *
 * Заказ по СБП оформляет сервер, когда банк подтвердит оплату, и происходит это уже без нашего
 * участия: вкладка могла закрыться, телефон — уйти в карман. Человек открывает приложение и
 * должен увидеть, что с его деньгами: код ещё ждёт оплаты, деньги получены и заказ оформляется,
 * банк отказал, деньги вернулись. Без этого блока оплаченный, но ещё не оформленный заказ выглядел
 * бы как «ничего не было» — и приглашал бы заплатить снова.
 *
 * Карточки в том же ряду и того же кроя, что `ActiveOrderCard`: это та же очередь, только на шаг
 * раньше. «Ожидает оплаты» несёт кнопку «Оплатить» — она открывает экран ожидания по
 * идентификатору, где код показывается заново, если заводили его на этом устройстве.
 *
 * Как и «Последние заказы», блок — украшение экрана, а не его состояние: не загрузился — его
 * просто нет. Опрос идёт только пока есть чего ждать, и только в открытой вкладке.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { queryKeys } from '@/app/query';
import { fetchPendingSbpPayments, type SbpPendingPayment } from '@/entities/order/sbp';
import { Button } from '@/shared/ui/Button';
import { Pill } from '@/shared/ui/Pill';
import { SbpIcon } from '@/shared/ui/icons';
import {
  sbpPendingPlaceLabel,
  sbpPendingSummary,
  sbpPendingUi,
  sbpPendingUnsettled,
} from './sbpPendingStatus';

/**
 * Чаще, чем заявки (25 с): здесь ждут ответа банка, и «оплата подтверждена» должна появиться,
 * пока человек ещё смотрит на экран, вернувшись из банковского приложения.
 */
export const SBP_PENDING_POLL_MS = 10_000;

export function PendingSbpPayments() {
  const queryClient = useQueryClient();

  const pending = useQuery({
    queryKey: queryKeys.sbpPending,
    queryFn: fetchPendingSbpPayments,
    retry: false,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((payment) => sbpPendingUnsettled(payment))
        ? SBP_PENDING_POLL_MS
        : false,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });

  /**
   * Платёж, которого ждали, перестал ждаться — стал заказом (или отказом). Заявки надо
   * перечитать сейчас, а не через 25 с их собственного опроса: иначе карточка отсюда пропадёт, а
   * заказ на экране ещё не появится, и человек полминуты смотрит на «ничего».
   */
  const awaited = useRef<Set<string>>(new Set());
  useEffect(() => {
    const items = pending.data;
    if (items === undefined) return;
    const stillAwaited = new Set(
      items.filter((payment) => sbpPendingUnsettled(payment)).map((payment) => payment.id),
    );
    const settled = [...awaited.current].some((id) => !stillAwaited.has(id));
    awaited.current = stillAwaited;
    if (settled) void queryClient.invalidateQueries({ queryKey: queryKeys.activeOrders });
  }, [pending.data, queryClient]);

  const items = pending.data ?? [];
  if (items.length === 0) return null;

  const now = Date.now();
  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="sr-only">Оплата по СБП</h2>
      <ul className="flex flex-col gap-2.5">
        {items.map((payment) => (
          <PendingSbpCard key={payment.id} payment={payment} now={now} />
        ))}
      </ul>
    </section>
  );
}

const CARD = 'flex items-center gap-3 rounded-md border border-line bg-surface px-4 py-[13px]';
const CARD_SHADOW = { boxShadow: 'var(--shadow-card)' };

function PendingSbpCard({ payment, now }: { payment: SbpPendingPayment; now: number }) {
  const navigate = useNavigate();
  const ui = sbpPendingUi(payment, now);

  const body = (
    <>
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-sm bg-surface-alt text-ink"
        aria-hidden="true"
      >
        <SbpIcon />
      </span>

      {/* `flex-auto`, а не `flex-1`: база колонки — её собственный текст, поэтому ряд честно
          переполняется и плашка справа отдаёт часть ширины (см. `Pill`). С `flex-1` (база 0)
          переполнения не бывает, и адресу оставалось 74-166 px из нужных 291. */}
      <div className="min-w-0 flex-auto">
        <h3 className="truncate text-[16px] font-semibold leading-[1.3125]">{ui.title}</h3>
        <p className="mt-0.5 truncate text-[12px] leading-[1.3333] text-ink-soft">
          {sbpPendingSummary(payment)}
        </p>
        {/* Адрес в две строки: «Москва, Ленинградский проспект, д. 39, стр. 79» — это 291 px, а на
            телефоне строке достаётся 74-166 px, и от адреса оставалось два слова, на узком — одно.
            Это единственная карточка, где адрес нужен целиком: по нему человек узнаёт, за какой
            именно неоплаченный заказ с него сейчас спросят денег. Межстрочие отношением (1.333 =
            те же 16px на 12px) — иначе при увеличенном системном шрифте вторая строка обрезается
            коробкой клампа. */}
        <p className="line-clamp-2 text-[12px] leading-[1.333] text-ink-faint">
          {sbpPendingPlaceLabel(payment, new Date(now))}
        </p>
        {ui.detail.length > 0 && (
          <p className="mt-0.5 text-[12px] leading-[1.3333] text-ink-soft">{ui.detail}</p>
        )}
      </div>

      {/* Polite: статус меняется под опросом, пока человек читает экран. Без обёртки: лишний
          flex-элемент между рядом и плашкой ломает дележ ширины (см. `Pill`). */}
      <Pill variant={ui.variant} live>
        {ui.pillLabel}
      </Pill>
    </>
  );

  // Заказ есть — карточка ведёт на него, как карточка активной заявки.
  if (ui.kind === 'placed' && payment.order_id !== null) {
    return (
      <li>
        <Link
          to="/order/$orderId"
          params={{ orderId: payment.order_id }}
          className={CARD}
          style={CARD_SHADOW}
        >
          {body}
        </Link>
      </li>
    );
  }

  // Оформляется — можно открыть ожидание и увидеть, как заказ появится.
  if (ui.kind === 'placing') {
    return (
      <li>
        <Link
          to="/order/sbp-return/$paymentId"
          params={{ paymentId: payment.id }}
          className={CARD}
          style={CARD_SHADOW}
        >
          {body}
        </Link>
      </li>
    );
  }

  // Ждёт оплаты — единственная карточка с действием. Кнопка под текстом, а не карточка-ссылка:
  // «Оплатить» должно читаться как кнопка, потому что за ней деньги.
  if (ui.kind === 'awaiting') {
    return (
      <li
        className="flex flex-col gap-2.5 rounded-md border border-line bg-surface px-4 py-[13px]"
        style={CARD_SHADOW}
      >
        <div className="flex items-center gap-3">{body}</div>
        <Button
          variant="secondary"
          onClick={() =>
            void navigate({ to: '/order/sbp-return/$paymentId', params: { paymentId: payment.id } })
          }
        >
          Оплатить
        </Button>
      </li>
    );
  }

  // Истёк, отказ, возврат — делать нечего, карточка просто говорит, что случилось.
  return (
    <li className={CARD} style={CARD_SHADOW}>
      {body}
    </li>
  );
}
